import styled from 'styled-components';
import { GoogleLogin } from 'react-google-login';
import FacebookLogin, {
  FacebookLoginClient,
} from '@greatsumini/react-facebook-login';
import AppleSignin from 'react-apple-signin-auth';
import { Box, Flex } from '@components/layout/Grid';
import useFlags from '@lib/hooks/useFlags';

const Title = styled.p`
  display: flex;
  align-items: center;
  color: #242424;
  font-size: 13px;
  text-transform: uppercase;
  line-height: 22px;
  opacity: 0.5;

  :after,
  :before {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #242424;
    opacity: 0.3;
  }

  :after {
    margin-left: 8px;
  }

  :before {
    margin-right: 8px;
  }
`;

const SocialButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6d6d6d;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.neutrals['gray-3']};
  border-radius: 3px;
  padding: 8px 12px;
  margin: 8px 5px;
  background: none;
  cursor: pointer;
  transition: border-color 200ms ease;

  :hover {
    border-color: ${({ theme }) => theme.colors.neutrals['gray']};
    box-shadow: 0px 1px 2px 0px
      ${({ theme }) => theme.colors.neutrals['gray-3']};
  }
`;

const Icon = styled.img`
  height: 24px;
  margin-right: 8px;
`;

function getFacebookProfile() {
  return new Promise((resolve, reject) => {
    const ID = setTimeout(reject, 5000);
    FacebookLoginClient.getProfile(
      profile => {
        clearTimeout(ID);
        const [firstName, lastName] = profile['name']?.split?.(' ');
        const picture = profile['picture']?.data?.url;
        resolve({ firstName, lastName, picture });
      },
      { fields: 'name,email,picture' }
    );
  });
}

type UserInfo = {
  firstName: string;
  lastName: string;
  picture?: string;
};

type Props = {
  title?: string;
  onFacebookAuth(token, userInfo: UserInfo): void;
  onAppleAuth(token, userInfo: UserInfo): void;
  onGoogleAuth(token, userInfo: UserInfo): void;
};
const SocialAuth = (props: Props) => {
  const [flags] = useFlags();
  const { title = 'Or sign up with' } = props;

  const responseGoogle = resp => {
    const firstName = resp.profileObj?.givenName;
    const lastName = resp.profileObj?.familyName;
    const picture = resp.profileObj?.imageUrl;
    props?.onGoogleAuth(resp['accessToken'], {
      firstName,
      lastName,
      picture,
    });
  };

  const onFacebookResponse = async resp => {
    const info = await getFacebookProfile();
    props?.onFacebookAuth(resp['accessToken'], {
      firstName: info['firstName'],
      lastName: info['lastName'],
      picture: info['picture'],
    });
  };

  const onAppleResponse = resp => {
    const { firstName, lastName } = resp?.user?.name || {};
    props?.onAppleAuth(resp?.authorization?.id_token, { firstName, lastName });
  };

  return (
    <Box>
      <Title>{title}</Title>
      <Flex my={20}>
        <GoogleLogin
          clientId={process.env.GOOGLE_CLIENT_ID}
          onSuccess={responseGoogle}
          responseType="token"
          scope="profile email"
          accessType="online"
          redirectUri={process.env.DOMAIN}
          uxMode="popup"
          render={renderProps => (
            <SocialButton
              {...renderProps}
              onClick={renderProps.onClick}
              disabled={renderProps.disabled}
            >
              <Icon src="/static/icons/google.svg" />
              Google
            </SocialButton>
          )}
        />
        <AppleSignin
          /** Auth options passed to AppleID.auth.init() */
          authOptions={{
            /** Client ID - eg: 'com.example.com' */
            clientId: process.env.APPLE_CLIENT_ID,
            /** Requested scopes, seperated by spaces - eg: 'email name' */
            scope: 'email name',
            /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
            redirectURI: process.env.DOMAIN,
            /** State string that is returned with the apple response */
            state: 'state',
            /** Nonce */
            nonce: 'nonce',
            /** Uses popup auth instead of redirection */
            usePopup: true,
          }} // REQUIRED
          /** General props */
          uiType="dark"
          /** className */
          className="apple-auth-btn"
          /** Removes default style tag */
          noDefaultStyle={false}
          /** Allows to change the button's children, eg: for changing the button text */
          buttonExtraChildren="Continue with Apple"
          /** Extra controlling props */
          /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
          onSuccess={onAppleResponse} // default = undefined
          /** Called upon signin error */

          /** Skips loading the apple script if true */
          skipScript={false} // default = undefined
          /** Apple image props */
          iconProp={{ style: { marginTop: '10px' } }} // default = undefined
          /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
          render={props => (
            <SocialButton {...props}>
              <Icon src="/static/icons/apple.svg" /> Apple
            </SocialButton>
          )}
        />
        <FacebookLogin
          appId={process.env.FACEBOOK_CLIENT_ID}
          scope="public_profile,email"
          dialogParams={{
            redirect_uri: process.env.DOMAIN,
            state: '',
          }}
          loginOptions={{
            return_scopes: true,
          }}
          onSuccess={onFacebookResponse}
          render={({ onClick }) => (
            <SocialButton onClick={onClick}>
              <Icon src="/static/icons/facebook.svg" /> Facebook
            </SocialButton>
          )}
        />
      </Flex>
    </Box>
  );
};

export default SocialAuth;
